import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomTab from '../Components/OsloKommuneUI/OKomTab';
import AuthImage from '../Components/AuthImage';
import { useUserContext } from '../Components/StateContext/UserContext';
import myconfig from '../Config';

const TAB_TAG = 'Tag bilder';
const TAB_UPLOAD = 'Last opp bilder';
const TABS = [TAB_TAG, TAB_UPLOAD];

const EVENT_OPTIONS = [
  { value: 'athletics', label: 'Friidrett' },
  { value: 'exam', label: 'Eksamen' },
  { value: 'aanduvila', label: 'Aandu Vila' },
  { value: 'thaipongal', label: 'Thai Pongal' },
  { value: 'annet', label: 'Annet' },
];
const YEARS: number[] = [];
for (let y = 2026; y <= 2036; y++) YEARS.push(y);

const eventKey = (p: Api.PhotoDto) => `${p.EventType}-${p.Year}`;

const thumbPlaceholder = (
  <div style={{
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f2f2f2', color: '#888', fontSize: '0.75rem', textAlign: 'center',
  }}>
    Behandles…
  </div>
);

// ---- Upload tab -----------------------------------------------------------------------------

const UploadTab: React.FC = () => {
  const [eventType, setEventType] = useState<string>('athletics');
  const [year, setYear] = useState<number>(YEARS[0]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<Api.PhotoUploadResultDto | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    setFiles(prev => [...prev, ...Array.from(incoming)]);
  };

  const removeFile = (idx: number) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const upload = async () => {
    if (files.length === 0) { toast.error('Velg minst én fil.'); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const token = localStorage.getItem('myToken');
      const res = await fetch(`${myconfig.rootUrl}/api/photos/Upload?eventType=${eventType}&year=${year}`, {
        method: 'POST',
        // No Content-Type header — the browser sets the multipart boundary itself.
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        let msg = `Feil: ${res.status}`;
        try { const j = await res.json(); if (j?.Message) msg = j.Message; } catch { /* ignore */ }
        toast.error(msg);
        return;
      }
      const data = await res.json() as Api.PhotoUploadResultDto;
      setResult(data);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      toast.success(`${data.Accepted.length} bilde(r) lastet opp. Miniatyrbilder genereres i bakgrunnen.`);
    } catch (e) {
      toast.error(`Opplasting feilet: ${e instanceof Error ? e.message : e}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ods-margin-top-5" style={{ maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
      <div className="ods-grid ods-grid--gap">
        <div className="ods-grid__column--6">
          <label className="ods-select__label" htmlFor="bilder-event">Arrangement</label>
          <div className="ods-select__select">
            <select id="bilder-event" value={eventType} onChange={e => setEventType(e.target.value)}>
              {EVENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="ods-grid__column--6">
          <label className="ods-select__label" htmlFor="bilder-year">År</label>
          <div className="ods-select__select">
            <select id="bilder-year" value={year} onChange={e => setYear(Number(e.target.value))}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); }}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="ods-margin-top-3"
        style={{
          border: '2px dashed #1f42aa', borderRadius: 8, padding: '2rem', textAlign: 'center',
          cursor: 'pointer', background: '#f7f9ff',
        }}
      >
        <p className="ods-text--size-kilo" style={{ margin: 0 }}>Dra og slipp bilder her, eller klikk for å velge</p>
        <p className="ods-color-text-grayscale-10" style={{ margin: '0.5rem 0 0' }}>JPEG, PNG eller WebP</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="ods-margin-top-3">
          <p className="ods-text--size-kilo">{files.length} fil(er) valgt:</p>
          <ul>
            {files.map((f, i) => (
              <li key={i}>
                {f.name}{' '}
                <a className="o-kom-link" onClick={() => removeFile(i)}>[fjern]</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ods-margin-top-3">
        <button className="ods-button" disabled={uploading || files.length === 0} onClick={upload}>
          {uploading ? 'Laster opp…' : 'Last opp'}
        </button>
      </div>

      {result && (
        <div className="ods-margin-top-5">
          {result.Accepted.length > 0 && (
            <p className="ods-color-text-green-dark">✓ {result.Accepted.length} bilde(r) lastet opp.</p>
          )}
          {result.Rejected.length > 0 && (
            <div>
              <p className="ods-color-text-red">{result.Rejected.length} bilde(r) ble avvist:</p>
              <ul>
                {result.Rejected.map((r, i) => <li key={i}>{r.FileName} — {r.Reason}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Tag tab --------------------------------------------------------------------------------

const TagTab: React.FC = () => {
  const [photos, setPhotos] = useState<Api.PhotoDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [parentResults, setParentResults] = useState<Api.TaggableParentDto[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await RestService.get<Api.PhotoDto[]>('/api/photos/List');
      setPhotos(data || []);
    } catch (e) {
      toast.error(`Kunne ikke hente bilder: ${e instanceof Error ? e.message : e}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const events = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number; year: number; eventType: string }>();
    photos.forEach(p => {
      const key = eventKey(p);
      const e = map.get(key) || { key, label: p.EventLabel, count: 0, year: p.Year, eventType: p.EventType };
      e.count++;
      map.set(key, e);
    });
    return Array.from(map.values()).sort((a, b) => b.year - a.year || a.eventType.localeCompare(b.eventType));
  }, [photos]);

  const filtered = useMemo(
    () => (selectedKey === 'all' ? photos : photos.filter(p => eventKey(p) === selectedKey)),
    [photos, selectedKey]
  );

  const selected = useMemo(() => photos.find(p => p.Id === selectedId) || null, [photos, selectedId]);
  const fellesTag = selected?.Tags.find(t => t.TagType === 'felles');

  const doSearch = async () => {
    try {
      setSearching(true);
      const q = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
      const data = await RestService.get<Api.TaggableParentDto[]>(`/api/photos/TaggableParents${q}`);
      setParentResults(data || []);
    } catch (e) {
      toast.error(`Søk feilet: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSearching(false);
    }
  };

  const addTag = async (photoId: number, body: Api.AddPhotoTagModel) => {
    setBusy(true);
    try {
      await RestService.post<Api.AddPhotoTagModel, any>(`/api/photos/${photoId}/Tags`, body);
      await load();
    } catch (e) {
      toast.error(`Kunne ikke legge til merkelapp: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (photoId: number, tagId: number) => {
    setBusy(true);
    try {
      await RestService.del<any>(`/api/photos/${photoId}/Tags/${tagId}`);
      await load();
    } catch (e) {
      toast.error(`Kunne ikke fjerne merkelapp: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleFelles = () => {
    if (!selected) return;
    if (fellesTag) removeTag(selected.Id, fellesTag.Id);
    else addTag(selected.Id, { TagType: 'felles' });
  };

  return (
    <div className="ods-grid ods-grid--gap ods-margin-top-5">
      {/* Left: filter + grid */}
      <div className="ods-grid__column--12 ods-grid__column--8-breakpoint-medium">
        <div style={{ marginBottom: '0.5rem' }}>
          <button
            className={`ods-button ${selectedKey === 'all' ? '' : 'ods-button--text'}`}
            onClick={() => setSelectedKey('all')}
          >
            Alle ({photos.length})
          </button>{' '}
          {events.map(e => (
            <button
              key={e.key}
              className={`ods-button ${selectedKey === e.key ? '' : 'ods-button--text'}`}
              onClick={() => setSelectedKey(e.key)}
            >
              {e.label} ({e.count})
            </button>
          ))}
        </div>

        {isLoading && <OKomLoading text='Laster bilder…' />}
        {!isLoading && filtered.length === 0 && <p className="ods-color-text-grayscale-10">Ingen bilder.</p>}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {filtered.map(p => {
            const isSel = p.Id === selectedId;
            const tagCount = p.Tags.length;
            return (
              <div
                key={p.Id}
                onClick={() => setSelectedId(p.Id)}
                title={p.OriginalFileName}
                style={{
                  width: 130, height: 130, overflow: 'hidden', cursor: 'pointer', position: 'relative',
                  border: isSel ? '3px solid #1f42aa' : '1px solid #ddd', borderRadius: 4, background: '#fafafa',
                }}
              >
                {p.ThumbUrl
                  ? <img src={p.ThumbUrl} alt={p.OriginalFileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : p.Status === 'processed'
                    ? <AuthImage path={`/api/photos/Image/${p.Id}/thumb`} placeholder={thumbPlaceholder}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : thumbPlaceholder}
                {tagCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2, background: '#2a2859', color: '#fff',
                    borderRadius: 8, fontSize: '0.7rem', padding: '0 0.35rem',
                  }}>{tagCount}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: tag editor for the selected photo */}
      <div className="ods-grid__column--12 ods-grid__column--4-breakpoint-medium">
        <h2 className="ods-text--size-kilo">Merkelapper</h2>
        {!selected && <p className="ods-color-text-grayscale-10">Velg et bilde for å tagge det.</p>}
        {selected && (
          <div>
            <p style={{ wordBreak: 'break-all' }}><strong>{selected.OriginalFileName}</strong><br />
              <span className="ods-color-text-grayscale-10">{selected.EventLabel}</span></p>

            <label style={{ display: 'block', marginBottom: '0.75rem' }}>
              <input type="checkbox" checked={!!fellesTag} disabled={busy} onChange={toggleFelles} />{' '}
              Felles (synlig for alle foreldre)
            </label>

            <h3 className="ods-text--size-kilo">Delt med foreldre</h3>
            <ul>
              {selected.Tags.filter(t => t.TagType === 'parent').map(t => (
                <li key={t.Id}>
                  {t.ParentName || t.ParentEmail || `Bruker ${t.ParentUserId}`}{' '}
                  <a className="o-kom-link" onClick={() => !busy && removeTag(selected.Id, t.Id)}>[fjern]</a>
                </li>
              ))}
              {selected.Tags.filter(t => t.TagType === 'parent').length === 0 && (
                <li className="ods-color-text-grayscale-10">Ingen.</li>
              )}
            </ul>

            <div className="ods-margin-top-3">
              <label className="ods-input__label" htmlFor="parent-search">Legg til forelder</label>
              <input
                id="parent-search"
                className="ods-input__input"
                placeholder="Søk på navn eller e-post"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              />
              <button className="ods-button ods-button--text ods-margin-top-1" disabled={searching} onClick={doSearch}>
                {searching ? 'Søker…' : 'Søk'}
              </button>
              <ul>
                {parentResults.map(p => (
                  <li key={p.Id}>
                    {p.Name || p.Email} <span className="ods-color-text-grayscale-10">({p.Email})</span>{' '}
                    <a className="o-kom-link" onClick={() => !busy && addTag(selected.Id, { TagType: 'parent', ParentUserId: p.Id })}>[legg til]</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Page -----------------------------------------------------------------------------------

const BilderConfigView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const canManage = userRoles.includes('admin') || userRoles.includes('localadmin');
  const [activeTab, setActiveTab] = useState<string>(TAB_TAG);

  useEffect(() => { document.title = 'Konfigurasjon — Bilder'; }, []);

  if (!canManage) {
    return (
      <>
        <OKomBreadcrumb path='Konfigurasjon, Bilder'></OKomBreadcrumb>
        <p className="ods-margin-top-5" style={{ padding: '0 1.5rem' }}>Du har ikke tilgang til denne siden.</p>
      </>
    );
  }

  return (
    <>
      <OKomBreadcrumb path='Konfigurasjon, Bilder'></OKomBreadcrumb>
      <div className="ods-grid ods-grid--center ods-margin-top-11">
        <div className="ods-grid__column--12 ods-text--align-center">
          <OKomTab tabs={TABS} onTabChange={setActiveTab} />
        </div>
      </div>
      <div className="ods-padding-top-3" style={{ padding: '0 1.5rem' }}>
        {activeTab === TAB_TAG && <TagTab />}
        {activeTab === TAB_UPLOAD && <UploadTab />}
      </div>
    </>
  );
};

export default withRouter(BilderConfigView);
